import { useEffect, useState, useRef } from 'react';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { productionBatchesSearch } from '@reducers/search';
import { ProductionBatch } from '@interfaces/search';
import { Events, TimelineDataWithLoadingState } from '@interfaces/timeline';
import { TIMELINE_BATCH_SELECT } from '@components/machines/components/timeline/constants';

interface UseEnhancedBatchesParams {
  batchesData: TimelineDataWithLoadingState<ProductionBatch[]>;
  events: Events;
}

interface UseEnhancedBatchesReturn {
  enhancedBatches: ProductionBatch[];
  enhancedBatchesData: TimelineDataWithLoadingState<ProductionBatch[]>;
}

export const useEnhancedBatches = ({ batchesData, events }: UseEnhancedBatchesParams): UseEnhancedBatchesReturn => {
  const dispatch = useAppDispatch();
  const [enhancedBatches, setEnhancedBatches] = useState<ProductionBatch[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const baseBatches = batchesData?.data || [];

  // Create simple hashes for dependencies to avoid array reference issues
  const baseBatchesHash = baseBatches.slice(0, 10).map(batch => batch.productionBatchId).join(',');
  const eventsHash = events.groupedByBatch?.slice(0, 10)?.map(event => event.productionBatchId).join(',') || '';

  useEffect(() => {
    (async () => {
      // Check if data is loaded before proceeding
      if (!batchesData.loaded || !events.loaded) {
        return;
      }

      if (!events.groupedByBatch?.length || !baseBatches) {
        setEnhancedBatches(baseBatches);
        return;
      }

      // Get all productionBatchIds from events
      const eventBatchIds = new Set<number>();
      events.groupedByBatch.forEach((event) => {
        if (event.productionBatchId) {
          eventBatchIds.add(event.productionBatchId);
        }
      });

      // Get existing productionBatchIds from batches
      const existingBatchIds = new Set(baseBatches.map((batch) => batch.productionBatchId));

      // Find missing productionBatchIds
      const missingBatchIds = Array.from(eventBatchIds).filter((id) => !existingBatchIds.has(id));

      if (missingBatchIds.length === 0) {
        setEnhancedBatches(baseBatches);
        return;
      }

      // Abort any previous request before creating new one
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        // Create OData filter for missing batch IDs
        const oDataFilter = missingBatchIds.map((id) => `ProductionBatchId eq ${id}`).join(' or ');

        const response = await dispatch(
          productionBatchesSearch({
            data: { select: TIMELINE_BATCH_SELECT },
            filter: { oDataFilter },
            select: undefined,
            resetHistory: true,
            signal: abortController.signal,
            ignoreHistory: true,
          })
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        const missingBatches = response?.searchResult || [];

        // Combine existing batches with fetched missing batches
        setEnhancedBatches([...baseBatches, ...missingBatches]);
      } catch (error) {
        const e = error as Error;
        // Don't log error if request was aborted
        if (e.name !== 'AbortError') {
          console.error('Failed to fetch missing batches:', error);
        }
        setEnhancedBatches(baseBatches);
      }
    })();

    // Cleanup function to abort pending requests on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [baseBatchesHash, eventsHash]);

  const enhancedBatchesData: TimelineDataWithLoadingState<ProductionBatch[]> = {
    loaded: batchesData.loaded,
    data: enhancedBatches,
  };

  return {
    enhancedBatches,
    enhancedBatchesData,
  };
};